<script setup>
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Button from 'dashboard/components-next/button/Button.vue';

defineProps({
  isSaving: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['saveChanges', 'continueEditing', 'discardAndLeave']);

const { t } = useI18n();

const dialogRef = ref(null);

// A "pick" flag distinguishes an explicit button choice from the dialog's
// own close event (Esc, click outside) — Dialog.close() fires `close`
// regardless of who triggered it, so without this flag every button click
// would also re-emit discardAndLeave via handleClose.
let actionPicked = false;

// Closing the dialog any other way than the explicit "X" (Esc, click
// outside) means the user wants to keep working on the page — same intent
// as "Continue editing", never a silent discard.
const handleClose = () => {
  if (actionPicked) {
    actionPicked = false;
    return;
  }
  emit('continueEditing');
};

const handleSaveChanges = () => {
  actionPicked = true;
  emit('saveChanges');
};

const handleContinueEditing = () => {
  actionPicked = true;
  dialogRef.value.close();
  emit('continueEditing');
};

const handleDiscardAndLeave = () => {
  actionPicked = true;
  dialogRef.value.close();
  emit('discardAndLeave');
};

defineExpose({ dialogRef });
</script>

<template>
  <Dialog
    ref="dialogRef"
    :title="t('CAPTAIN.ASSISTANTS.UNSAVED_CHANGES.TITLE')"
    :show-cancel-button="false"
    :show-confirm-button="false"
    @close="handleClose"
  >
    <template #description>
      <div class="flex items-start justify-between w-full gap-3">
        <p class="mb-0 text-sm text-n-slate-11">
          {{ t('CAPTAIN.ASSISTANTS.UNSAVED_CHANGES.DESCRIPTION') }}
        </p>
        <Button
          icon="i-lucide-x"
          variant="ghost"
          color="slate"
          size="sm"
          class="shrink-0 -mt-1 -mr-1"
          :disabled="isSaving"
          @click="handleDiscardAndLeave"
        />
      </div>
    </template>
    <template #footer>
      <div class="flex items-center justify-between w-full gap-3">
        <Button
          :label="t('CAPTAIN.ASSISTANTS.UNSAVED_CHANGES.SAVE_CHANGES')"
          class="w-full"
          :is-loading="isSaving"
          :disabled="isSaving"
          @click="handleSaveChanges"
        />
        <Button
          variant="faded"
          color="slate"
          :label="t('CAPTAIN.ASSISTANTS.UNSAVED_CHANGES.CONTINUE_EDITING')"
          class="w-full"
          :disabled="isSaving"
          @click="handleContinueEditing"
        />
      </div>
    </template>
  </Dialog>
</template>
